<script setup lang="ts">
import { RouterLink, useRoute } from 'vue-router'
import { useGameStore } from '@/stores/gameStore.ts'
import { usePlayerRelationStore } from '@/stores/playerRelations.ts'
import { computed, ref, onMounted, watch } from 'vue'
import { version } from '@/../package.json'
import HeaderButton from '@/components/Elements/Buttons/HeaderButton.vue' // Adjust path if necessary
import router from '@/router'
import { useChatStore } from '@/stores/chatStore.ts'
import { useAllianceStore } from '@/stores/allianceStore.ts'
import PatreonIcon from '@/components/Elements/Images/Icons/PatreonIcon.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

// Define a multi-word component name
defineOptions({
  name: 'MainHeader'
})


const route = useRoute()

const gameStore = useGameStore()
const playerRelations = usePlayerRelationStore()
const menuVisible = ref(false)
const chatStore = useChatStore()
const allianceStore = useAllianceStore()

const totalDiplomacyRequests = computed(() => {
  // Count of player relation requests (NAP, friendship, etc.)
  const relationRequests = playerRelations.getCountOfPendingActions()

  // Count of alliance join requests (only for alliance leaders)
  let allianceJoinRequests = 0

  // Check if player is in an alliance and is the leader
  if (gameStore.player?.allianceId) {
    const alliance = allianceStore.alliances.get(gameStore.player.allianceId)
    if (alliance?.leaderPlayerId === gameStore.player?.id && alliance.joinRequests) {
      allianceJoinRequests = alliance.joinRequests.length
    }
  }

  return relationRequests + allianceJoinRequests
})

const totalCommunicationRequests = computed(() => {
  return chatStore.getCountOfUnreadMessages()
})

const buttons = [
  { key: 'map', target: '/map/galaxy', activeKey: 'Map' },
  { key: 'planets', target: '/planets', activeKey: 'Planets' },
  { key: 'command', target: '/command/ships', activeKey: 'Command' },
  { key: 'research', target: '/research', activeKey: 'Research' },
  { key: 'diplomacy', target: '/diplomacy/overview', activeKey: 'Diplomacy' },
  { key: 'government', target: '/government/cabinet', activeKey: 'Government' },
  { key: 'resources', target: '/resources', activeKey: 'Resources' },
  { key: 'statistics', target: '/stats', activeKey: 'Statistics' },
  { key: 'leaderboard', target: '/leaderboard', activeKey: 'Leaderboard' },
  { key: 'news', target: '/news', activeKey: 'News' },
  { key: 'communication', target: '/communication', activeKey: 'Communication' },
  { key: 'settings', target: '/settings', activeKey: 'Settings' }
]

const goBack = () => {
  router.replace(gameStore.routerLastUrl)
}

const goHome = () => {
  window.location.href = 'https://ferion.com/dashboard'
}

onMounted(() => {
  document.title = 'Ferion Beta ' + version
})

// Hide mobile menu when game goes out of sync
watch(() => gameStore.gameOutOfSync, (isOutOfSync) => {
  if (isOutOfSync) {
    menuVisible.value = false
  }
})
</script>

<template>
  <div v-if="menuVisible" @click="menuVisible = !menuVisible" class="fixed w-full h-full">
    <!-- Close menu on background click -->
  </div>
  <div class="header bg-slate-700 flex sm:justify-start justify-between text-white">
    <div  class="block sm:hidden self-center ps-2">
      <HeaderButton @click="goBack">{{ t('common.navigation.back') }}</HeaderButton>
    </div>
    <!-- Logo -->
    <div @click="goHome" class="cursor-pointer ps-2 m-2">
      <object
        data="https://cdn.ferion.com/images/logos/logo.svg"
        type="image/svg+xml"
        class="pointer-events-none"
        width="130"
        height="auto"
      ></object>
    </div>
    <!-- Menu -->
    <div
      :class="{
        'text-nowrap text-center sm:text-left rounded-b': true,
        'hidden sm:flex sm:self-center self-top pl-5': !menuVisible,
        'absolute flex flex-col bg-slate-600 z-50 pt-1 px-1 top right-0': menuVisible
      }"
    >
      <RouterLink v-for="button in buttons" :key="button.key" :to="button.target">
        <div>
          <HeaderButton
            :active="typeof route.name === 'string' && route.name.startsWith(button.activeKey)"
            @click="menuVisible = false"
            class="me-0 sm:me-1 mb-1 sm:mb-0 w-full sm:w-auto"
          >
            <span class="sm:hidden xl:inline">{{ t('common.navigation.' + button.key) }}</span>
            <span class="hidden sm:inline xl:hidden">{{ t('common.navigation.' + button.key + 'Short') }}</span>
            <span
              v-if="button.activeKey === 'News' && Number(gameStore.player?.unreadNewsItems) > 0"
              class="rounded bg-red-500 text-white text-xs px-1 ml-1"
              >{{ gameStore.player?.unreadNewsItems }}</span
            >

            <span
              v-if="button.activeKey === 'Diplomacy' && totalDiplomacyRequests > 0"
              class="rounded bg-red-500 text-white text-xs px-1 ml-1"
              >{{ totalDiplomacyRequests }}</span
            >

            <span
              v-if="button.activeKey === 'Communication' && totalCommunicationRequests > 0"
              class="rounded bg-red-500 text-white text-xs px-1 ml-1"
              >{{ totalCommunicationRequests }}</span
            >
          </HeaderButton>
        </div>
      </RouterLink>
      <!-- Support Link (Mobile Only in Menu) -->
      <div class="sm:hidden">
        <a
          href="https://ferion.com/patreon"
          target="_blank"
          rel="noopener noreferrer"
          @click="menuVisible = false"
        >
          <HeaderButton class="mb-1 w-full">
            <span>{{ gameStore.player?.patreonTier && gameStore.player.patreonTier !== 'none' ? gameStore.player.patreonTier : t('common.navigation.support') }}</span>
            <PatreonIcon :patreon-tier="gameStore.player?.patreonTier || 'none'" :size="14" :hide-if-none="false" />
          </HeaderButton>
        </a>
      </div>
    </div>
    <!-- Support Link (Desktop Only) -->
    <div class="hidden sm:flex self-center ml-auto pe-2">
      <a
        href="https://ferion.com/patreon"
        target="_blank"
        rel="noopener noreferrer"
      >
        <HeaderButton>
          <PatreonIcon :patreon-tier="gameStore.player?.patreonTier || 'none'" :size="14" :hide-if-none="false" class="!ml-0" />
        </HeaderButton>
      </a>
    </div>
    <div class="block sm:hidden self-center pe-2">
      <HeaderButton @click="menuVisible = !menuVisible">
        <span v-if="Number(gameStore.player?.unreadNewsItems) > 0" class="border-b border-b-red-500"
          >{{ t('common.navigation.menu') }}</span
        >
        <span v-else>{{ t('common.navigation.menu') }}</span>
      </HeaderButton>
    </div>
  </div>
</template>

<style scoped>
.header {
  height: 2.5rem;
}
.top {
  top: 2.5rem;
}
</style>
